import * as _ from 'lodash';
import moment from 'moment';
import { ApiService } from '../services/api.service';

export class ValidationService {
  constructor(intl) {
    this.intl = intl;
  }

  isValidUsername(value) {
    value = _.trim(value);

    var errorMessageStr = [];

    // length
    if (value.length < 6 || value.length > 31) {
      errorMessageStr.push(
        this.intl.formatMessage({ id: 'validationError.6to30characters' })
      );
    }

    // 1 letter
    if (!/[a-zA-Z]/.test(value)) {
      errorMessageStr.push(
        this.intl.formatMessage({ id: 'validationError.leastOneLetter' })
      );
    }

    // no special characters
    if (/[#;&,.+*~%_\-`\':"!^${}[\]()=>|\/@\ ]/.test(value)) {
      errorMessageStr.push(
        this.intl.formatMessage({
          id: 'validationError.noSpacesOrSpecialChars',
        })
      );
    }

    errorMessageStr = _.capitalize(
      _.trimStart(errorMessageStr.join(', '), ', ')
    );

    return {
      valid: !errorMessageStr.length,
      errorMessage: errorMessageStr.length ? errorMessageStr : null,
    };
  }

  isValidEmail(value) {
    value = _.trim(value);

    let errorMessageStr = [];

    // email format
    var format =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    if (!format.test(value)) {
      errorMessageStr.push(
        this.intl.formatMessage({ id: 'validationError.validEmail' })
      );
    }

    errorMessageStr = _.capitalize(
      _.trimStart(errorMessageStr.join(', '), ', ')
    );

    return {
      valid: !errorMessageStr.length,
      errorMessage: errorMessageStr.length ? errorMessageStr : null,
    };
  }

  isValidPassword(value, username) {
    value = _.trim(value);
    username = _.trim(username);

    let errorMessageStr = '';

    // Password shouldn't be the same as username
    if (value === username) {
      errorMessageStr = this.intl.formatMessage({
        id: 'validationError.differentUsername',
      });
    }
    // Password length should be greater than 8
    else if (value.length < 8 || value.length > 20) {
      errorMessageStr = this.intl.formatMessage({
        id: 'validationError.8to20characters',
      });
    }
    // Password should have a letter
    else if (!/[a-zA-Z]/.test(value)) {
      errorMessageStr = this.intl.formatMessage({
        id: 'validationError.min1letter',
      });
    }
    // Password must have a number 0-9
    else if (!/[0-9]/.test(value)) {
      errorMessageStr = this.intl.formatMessage({
        id: 'validationError.min1number',
      });
    }
    // Password must have at least one uppercase letter
    else if (!/[A-Z]/.test(value)) {
      errorMessageStr = this.intl.formatMessage({
        id: 'validationError.requiredUppercase',
      });
    } else if (!/[a-z]/.test(value)) {
      errorMessageStr = this.intl.formatMessage({
        id: 'validationError.requiredLowercase',
      });
    } else if (!/.*[!@#$%^&*]+/.test(value)) {
      errorMessageStr = this.intl.formatMessage({
        id: 'validationError.requiredSpecialCharacter',
      });
    }

    errorMessageStr = _.capitalize(_.trimStart(errorMessageStr));

    return {
      valid: !errorMessageStr.length,
      errorMessage: errorMessageStr.length ? errorMessageStr : null,
    };
  }

  isValidConfirmPassword(value, password) {
    value = _.trim(value);
    password = _.trim(password);

    let errorMessageStr = [];

    if (value != password) {
      errorMessageStr.push('Must match password');
    }

    errorMessageStr = _.capitalize(
      _.trimStart(errorMessageStr.join(', '), ', ')
    );

    return {
      valid: !errorMessageStr.length,
      errorMessage: errorMessageStr.length ? errorMessageStr : null,
    };
  }

  isValidZip(value) {
    let errorMessageStr = [];

    if (value.length < 5 || value.length >= 6) {
      errorMessageStr.push(
        this.intl.formatMessage({ id: 'validationError.zip5characters' })
      );
    }

    if (
      !/(^\d{5}(-\d{4})?$)|(^[ABCEGHJKLMNPRSTVXYabceghjklmnpstvxy]{1}\d{1}[A-Za-z]{1} ?\d{1}[A-Za-z]{1}\d{1})$/.test(
        value
      )
    ) {
      errorMessageStr.push(
        this.intl.formatMessage({ id: 'validationError.zipNumbersOnly' })
      );
    }

    errorMessageStr = _.capitalize(
      _.trimStart(errorMessageStr.join(', '), ', ')
    );

    return {
      valid: !errorMessageStr.length,
      errorMessage: errorMessageStr.length ? errorMessageStr : null,
    };
  }

  isValidPhone(value) {
    let errorMessageStr = [];

    if (!/^[0-9]{10}$/.test(value)) {
      errorMessageStr.push(
        this.intl.formatMessage({ id: 'validationError.phone10digits' })
      );
    }

    if (/[!|@|#|$|%|^|&|*(|)|-|=|+]/.test(value)) {
      errorMessageStr.push(
        this.intl.formatMessage({ id: 'validationError.phoneDigitsOnly' })
      );
    }

    errorMessageStr = _.capitalize(
      _.trimStart(errorMessageStr.join(', '), ', ')
    );

    return {
      valid: !errorMessageStr.length,
      errorMessage: errorMessageStr.length ? errorMessageStr : null,
    };
  }

  isRequiredValid(value) {
    let valid = _.size(_.toString(value)) ? true : false;
    return {
      valid,
      errorMessage: valid
        ? ''
        : this.intl.formatMessage({ id: 'validationError.required' }),
    };
  }

  isValidDate(date, format) {
    // trick to avoid momentjs warning on invalid dates
    let warn = console.warn;
    console.warn = () => {};

    format = format ? format : 'MM/DD/YYYY';
    let valid = moment(date, format).isValid();

    // put the warn back to normal
    console.warn = warn;

    // check is not a date in the future
    if (valid) {
      if (
        moment(date).isAfter(moment()) ||
        moment(date).isBefore('1900-01-01')
      ) {
        valid = false;
      }
    }

    return {
      valid,
      value: valid ? moment(date).format(format) : null,
      errorMessage: valid
        ? ''
        : this.intl.formatMessage({ id: 'validationError.invalidDate' }),
    };
  }
}



// WEBPACK FOOTER //
// ./registration-react/validation.service.js